// Data source identifiers
// Used to identify the origin of listings
export const DATA_SOURCE = {
  ORGANIC: 1, // Regular user-created listings
  RESCUEGROUPS: 30, // RescueGroups pet adoption API
  SPORTSERVER: 35, // SportServer sports merchandise
  CJ_DROPSHIPPING: 40, // CJ Dropshipping imported + on-platform fulfilled (34 was contaminated by stale petfinder metadata)
  ADMIN_TEST: 99, // Admin test products (hidden from search)
} as const;

export type DataSource = (typeof DATA_SOURCE)[keyof typeof DATA_SOURCE];

/**
 * Human names for every data source id that has appeared in the index, for the
 * dash. Ids that have been retired stay listed so an old document still reads.
 */
export const DATA_SOURCE_LABELS: Readonly<Record<number, string>> = Object.freeze({
  1: 'Organic (sellers and Vendoo)',
  27: 'Gateway Classic Cars',
  28: 'MyEV',
  29: 'Sella',
  30: 'RescueGroups',
  31: 'LotLinx',
  33: 'BargainHunter',
  34: 'RescueGroups (staging)',
  35: 'SportServer',
  40: 'CJ Dropshipping',
  99: 'Admin test',
});

export function dataSourceLabel(id: number | null | undefined): string {
  if (id === null || id === undefined) return 'Unknown source';
  return DATA_SOURCE_LABELS[id] ?? `Source ${id}`;
}

/**
 * Sources whose photos still live in `LISTINGS_METADATA.photo0..photo7` as
 * foreign-hosted URLs, served to the browser verbatim.
 *
 * Everything NOT in this list resolves photos through `getListingPhotos()` —
 * our S3 keys, the resize Lambda's webp variants, and the
 * `listings-static{1..4}.sellwild.com` CDN.
 *
 * This list shrinks to `[]` as each feed is migrated onto the organic pipe;
 * see docs/EXTERNAL_PHOTO_MIGRATION_PLAN.md. Retiring a source is a one-line
 * edit here, NOT a sweep through 20 call sites — keep it that way.
 *
 * Left, 2026-10-24: 34, staging's RescueGroups (prod has no id 34). Every
 * prod feed is on our bucket: 27 gatewayclassiccars (1,754 active, the last 13
 * backfilled 2026-10-24), 31 lotlinx (23,878) and 30 RescueGroups (12,630),
 * the CDN copies probed 200; 28 myev / 29 sella are dead feeds with no active
 * listing. Their importers mirror on import (utils/import-suppliers.ts
 * createListingMetaData), so nothing new arrives foreign.
 */
export const METADATA_PHOTO_SOURCES: readonly number[] = [34];

/**
 * True when this listing's photos must be read from LISTINGS_METADATA rather
 * than generated from its listing id.
 */
export const usesMetadataPhotos = (
  dataSourceId: number | string | undefined | null
): boolean => {
  if (dataSourceId === undefined || dataSourceId === null || dataSourceId === '') return false;
  const id = Number(dataSourceId);
  return Number.isFinite(id) && METADATA_PHOTO_SOURCES.includes(id);
};

// Helper to check if listing is from SellWild (RescueGroups)
export const isSellWildListing = (dataSourceId: number | undefined | null): boolean => {
  return dataSourceId === DATA_SOURCE.RESCUEGROUPS;
};

// Helper to check if listing is an adoption listing (RescueGroups)
export const isAdoptionListing = (dataSourceId: number | undefined | null): boolean => {
  return dataSourceId === DATA_SOURCE.RESCUEGROUPS;
};

// Helper to check if listing is an admin test product
export const isAdminTestListing = (dataSourceId: number | undefined | null): boolean => {
  return dataSourceId === DATA_SOURCE.ADMIN_TEST;
};
